'use client';

import { BadgeCheck, Hash, Lock, RefreshCw, Save, UserRoundCheck, Users } from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	EmployeeNumberBulkAssignmentResponse,
	EmployeeNumberPolicyResponse,
	UpdateEmployeeNumberPolicyRequest
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type PolicyFormState = {
	autoGenerationEnabled: boolean;
	editLocked: boolean;
	prefix: string;
	digits: string;
	nextSequence: string;
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function formFromPolicy(policy: EmployeeNumberPolicyResponse): PolicyFormState {
	return {
		autoGenerationEnabled: policy.autoGenerationEnabled,
		editLocked: policy.editLocked,
		prefix: policy.prefix,
		digits: String(policy.digits),
		nextSequence: String(policy.nextSequence)
	};
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: string | number;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

export function EmployeeNumberPolicyPage() {
	const [state, setState] = useState<RequestState<EmployeeNumberPolicyResponse>>({ status: 'idle' });
	const [bulkState, setBulkState] = useState<RequestState<EmployeeNumberBulkAssignmentResponse>>({
		status: 'idle'
	});
	const [form, setForm] = useState<PolicyFormState>({
		autoGenerationEnabled: false,
		editLocked: false,
		prefix: 'EMP-',
		digits: '4',
		nextSequence: '1'
	});
	const [message, setMessage] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [assigning, setAssigning] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			setBulkState({ status: 'loading' });
			setMessage(null);
			try {
				const [policy, bulkPreview] = await Promise.all([
					api.get<EmployeeNumberPolicyResponse>('/employees/number-policy'),
					api.get<EmployeeNumberBulkAssignmentResponse>('/employees/number-policy/bulk-preview')
				]);
				setState({ status: 'success', data: policy });
				setBulkState({ status: 'success', data: bulkPreview });
				setForm(formFromPolicy(policy));
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
				setBulkState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSaving(true);
		setMessage(null);
		try {
			const request: UpdateEmployeeNumberPolicyRequest = {
				autoGenerationEnabled: form.autoGenerationEnabled,
				editLocked: form.editLocked,
				prefix: form.prefix.trim() || 'EMP-',
				digits: Number(form.digits),
				nextSequence: Number(form.nextSequence)
			};
			const policy = await api.put<EmployeeNumberPolicyResponse>('/employees/number-policy', request);
			const bulkPreview = await api.get<EmployeeNumberBulkAssignmentResponse>(
				'/employees/number-policy/bulk-preview'
			);
			setState({ status: 'success', data: policy });
			setBulkState({ status: 'success', data: bulkPreview });
			setForm(formFromPolicy(policy));
			setMessage('사번 정책을 저장했습니다.');
		} catch (error) {
			setMessage(getErrorMessage(error));
		} finally {
			setSaving(false);
		}
	}

	async function assignMissingEmployeeNumbers() {
		setAssigning(true);
		setMessage(null);
		try {
			const result = await api.post<EmployeeNumberBulkAssignmentResponse>(
				'/employees/number-policy/bulk-assign'
			);
			setBulkState({ status: 'success', data: { ...result, targetCount: 0, items: [] } });
			setMessage(`${numberLabel(result.assignedCount)}명에게 사번을 부여했습니다.`);
			if (data) {
				const next = {
					...data,
					nextSequence: result.nextSequence,
					previewNumber: result.nextPreviewNumber,
					missingEmployeeNumberCount: Math.max(
						0,
						data.missingEmployeeNumberCount - result.assignedCount
					)
				};
				setState({ status: 'success', data: next });
				setForm(formFromPolicy(next));
			}
		} catch (error) {
			setMessage(getErrorMessage(error));
		} finally {
			setAssigning(false);
		}
	}

	const data = state.status === 'success' ? state.data : null;
	const bulk = bulkState.status === 'success' ? bulkState.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden pb-28 lg:pb-0">
			<PageHeader
				title="사번 정책"
				description="직원 생성 시 사번을 자동 부여하고, 운영 중 사번 변경을 잠가 급여·외부 시스템 식별자 오류를 줄입니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{message ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{message}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="다음 사번"
							value={data.previewNumber}
							description={`${data.prefix} + ${data.digits}자리 순번`}
							icon={Hash}
						/>
						<StatCard
							title="전체 직원"
							value={data.totalEmployeeCount}
							description={`미부여 ${numberLabel(data.missingEmployeeNumberCount)}명`}
							icon={Users}
						/>
						<StatCard
							title="자동생성"
							value={data.autoGenerationEnabled ? '사용' : '중지'}
							description="직원 등록 시 빈 사번 자동 부여"
							icon={BadgeCheck}
						/>
						<StatCard
							title="수정 잠금"
							value={data.editLocked ? '잠금' : '허용'}
							description="기존 사번 변경 통제"
							icon={Lock}
						/>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div>
									<CardTitle>자동생성 규칙</CardTitle>
									<CardDescription>접두어, 자리수, 다음 순번과 사번 수정 잠금 상태를 관리합니다.</CardDescription>
								</div>
								<Badge variant={data.editLocked ? 'success' : 'warning'}>
									{data.editLocked ? '수정 잠금' : '수정 허용'}
								</Badge>
							</div>
						</CardHeader>
						<CardContent>
							<form className="grid gap-4" onSubmit={submit}>
								<div className="grid gap-4 md:grid-cols-3">
									<div>
										<Label htmlFor="employee-number-prefix">접두어</Label>
										<Input
											id="employee-number-prefix"
											value={form.prefix}
											onChange={(event) => setForm((current) => ({ ...current, prefix: event.target.value }))}
											maxLength={20}
										/>
									</div>
									<div>
										<Label htmlFor="employee-number-digits">자리수</Label>
										<Input
											id="employee-number-digits"
											type="number"
											min={2}
											max={10}
											value={form.digits}
											onChange={(event) => setForm((current) => ({ ...current, digits: event.target.value }))}
										/>
									</div>
									<div>
										<Label htmlFor="employee-number-next-sequence">다음 순번</Label>
										<Input
											id="employee-number-next-sequence"
											type="number"
											min={1}
											value={form.nextSequence}
											onChange={(event) =>
												setForm((current) => ({ ...current, nextSequence: event.target.value }))
											}
										/>
									</div>
								</div>
								<div className="grid gap-3 md:grid-cols-2">
									<label className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm">
										<input
											type="checkbox"
											checked={form.autoGenerationEnabled}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													autoGenerationEnabled: event.target.checked
												}))
											}
										/>
										<span>
											<span className="font-medium">사번 자동생성</span>
											<span className="block text-xs text-muted-foreground">
												직원 등록 시 사번이 비어 있으면 다음 번호를 부여합니다.
											</span>
										</span>
									</label>
									<label className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm">
										<input
											type="checkbox"
											checked={form.editLocked}
											onChange={(event) =>
												setForm((current) => ({ ...current, editLocked: event.target.checked }))
											}
										/>
										<span>
											<span className="font-medium">수정 잠금</span>
											<span className="block text-xs text-muted-foreground">
												이미 부여된 사번이 급여·외부 시스템과 어긋나지 않게 변경을 막습니다.
											</span>
										</span>
									</label>
								</div>
								<div className="flex justify-end">
									<Button type="submit" disabled={saving}>
										<Save className="size-4" />
										정책 저장
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>

					<Card className="min-w-0">
						<CardHeader>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div>
									<CardTitle>미부여 사번 일괄 부여</CardTitle>
									<CardDescription>
										사번이 비어 있는 기존 직원에게 현재 정책의 다음 번호를 순서대로 부여합니다.
									</CardDescription>
								</div>
								<Badge variant={bulk && bulk.targetCount > 0 ? 'warning' : 'success'}>
									대상 {numberLabel(bulk?.targetCount)}명
								</Badge>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 sm:grid-cols-3">
								<div className="rounded-md border border-border bg-muted/20 p-3">
									<p className="text-xs text-muted-foreground">미리보기 대상</p>
									<p className="mt-1 text-xl font-semibold">{numberLabel(bulk?.targetCount)}명</p>
								</div>
								<div className="rounded-md border border-border bg-muted/20 p-3">
									<p className="text-xs text-muted-foreground">부여 시작</p>
									<p className="mt-1 break-words text-xl font-semibold">
										{bulk?.items[0]?.assignedEmployeeNumber ?? '-'}
									</p>
								</div>
								<div className="rounded-md border border-border bg-muted/20 p-3">
									<p className="text-xs text-muted-foreground">적용 후 다음</p>
									<p className="mt-1 break-words text-xl font-semibold">
										{bulk?.targetCount ? bulk.nextPreviewNumber : data.previewNumber}
									</p>
								</div>
							</div>

							{bulkState.status === 'loading' || bulkState.status === 'idle' ? (
								<div className="flex min-h-28 items-center justify-center rounded-md border border-border">
									<Spinner size="md" />
								</div>
							) : null}

							{bulkState.status === 'error' ? (
								<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
									{bulkState.message}
								</div>
							) : null}

							{bulk && bulk.items.length > 0 ? (
								<div className="overflow-x-auto rounded-md border border-border">
									<table className="min-w-full text-left text-sm">
										<thead className="bg-muted/40 text-xs text-muted-foreground">
											<tr>
												<th className="px-4 py-3 font-medium">직원</th>
												<th className="px-4 py-3 font-medium">조직</th>
												<th className="px-4 py-3 font-medium">부여 예정 사번</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-border">
											{bulk.items.slice(0, 10).map((item) => (
												<tr key={item.employeeId}>
													<td className="px-4 py-3">
														<p className="font-medium">{item.employeeName}</p>
														<p className="text-xs text-muted-foreground">{item.position ?? '-'}</p>
													</td>
													<td className="px-4 py-3 text-muted-foreground">{item.department ?? '-'}</td>
													<td className="px-4 py-3 font-mono font-semibold">
														{item.assignedEmployeeNumber}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : null}

							{bulk && bulk.items.length === 0 ? (
								<div className="rounded-md border border-dashed border-border bg-muted/20 p-5 text-sm text-muted-foreground">
									사번 미부여 직원이 없습니다.
								</div>
							) : null}

							<div className="flex justify-end">
								<Button
									type="button"
									disabled={!bulk || bulk.targetCount === 0 || assigning}
									onClick={() => void assignMissingEmployeeNumbers()}
								>
									{assigning ? <Spinner size="sm" /> : <UserRoundCheck className="size-4" />}
									일괄 부여
								</Button>
							</div>
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
